/**
 * EVORIA DISCOUNT SYSTEM
 *
 * Flow:
 * 1. User enters discount code -> Apply real Shopify discount (visible in cart)
 * 2. Cart shows the discount code normally via Shopify
 * 3. At checkout: Remove discount, swap product to promo variant
 * 4. Checkout shows promo variant with discounted price (from Seal subscription)
 * 5. On return from checkout: Reapply discount if needed
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'evoria_stored_discount_code';
  const ORIGINAL_CART_KEY = 'evoria_original_cart_state';
  let isProcessing = false;
  let isProcessingReturn = false; // Flag to prevent multiple return-from-checkout handlers

  // ============================================================================
  // XHR FETCH (bypass third-party interceptors)
  // ============================================================================

  function xhrFetch(url, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const method = options.method || 'GET';

      try {
        xhr.open(method, url, true);
      } catch (e) {
        reject(new Error('Invalid URL: ' + url));
        return;
      }

      if (options.headers) {
        for (const [key, value] of Object.entries(options.headers)) {
          if (typeof value === 'string') {
            try {
              xhr.setRequestHeader(key, value);
            } catch (e) {}
          }
        }
      }

      xhr.onload = () => {
        const response = {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: async () => JSON.parse(xhr.responseText),
          text: async () => xhr.responseText
        };
        resolve(response);
      };

      xhr.onerror = () => reject(new TypeError('XHR request failed'));
      xhr.timeout = 10000;
      xhr.ontimeout = () => reject(new TypeError('XHR request timed out'));
      xhr.send(options.body);
    });
  }

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================

  async function getCart() {
    const response = await xhrFetch('/cart.js');
    return response.json();
  }

  async function applyShopifyDiscount(discountCode) {
    const response = await xhrFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: discountCode })
    });

    if (!response.ok) {
      throw new Error('Failed to apply discount');
    }

    return response.json();
  }

  async function clearShopifyDiscount() {
    await xhrFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    });

    // Poll to verify discount is cleared (reduced from 5s to 1s max)
    for (let i = 0; i < 10; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      const cart = await getCart();
      if (cart.discount_codes.length === 0) {
        return cart;
      }
    }

    return await getCart();
  }

  async function addVariantToCart(variantId, quantity, sellingPlan = null) {
    const body = {
      id: variantId,
      quantity: quantity
    };

    if (sellingPlan) {
      body.selling_plan = sellingPlan;
    }

    const response = await xhrFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    return response.json();
  }

  // Batch add multiple items at once (much faster than sequential adds)
  async function addVariantsToCart(items) {
    const body = {
      items: items.map(item => {
        const entry = {
          id: item.variantId,
          quantity: item.quantity
        };
        if (item.sellingPlan) {
          entry.selling_plan = item.sellingPlan;
        }
        return entry;
      })
    };

    const response = await xhrFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    return response.json();
  }

  // Batch remove multiple items at once (parallel individual removes for reliability)
  async function removeItemsFromCart(itemKeys) {
    // Send all remove requests in parallel
    await Promise.all(itemKeys.map(key =>
      xhrFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(r => r.json())
    ));

    // Verify items are actually removed
    for (let i = 0; i < 12; i++) {
      await new Promise(resolve => setTimeout(resolve, 80));
      const cart = await getCart();
      const remainingKeys = itemKeys.filter(key => cart.items.some(item => item.key === key));
      if (remainingKeys.length === 0) {
        refreshCartUI();
        return;
      }
    }

    // Log warning if any items remain
    const cart = await getCart();
    const remainingKeys = itemKeys.filter(key => cart.items.some(item => item.key === key));
    if (remainingKeys.length > 0) {
      console.warn('[EvoriaDiscount] Some items may not have been removed:', remainingKeys);
    }
    refreshCartUI();
  }

  async function removeItemFromCart(itemKey) {
    const response = await xhrFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 })
    });

    await response.json();
    refreshCartUI();
  }

  // ============================================================================
  // PROMO VARIANT LOOKUP
  // ============================================================================

  async function getPromoVariant(baseHandle, discountCode, originalSellingPlanId = null) {
    console.log('[EvoriaDiscount] getPromoVariant called with:', { baseHandle, discountCode, originalSellingPlanId });

    if (!window.EvoriaDiscountConfig) {
      console.error('[EvoriaDiscount] EvoriaDiscountConfig not loaded');
      return null;
    }

    // Get the discount config for this specific product (supports universal discount codes)
    const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(discountCode, baseHandle);
    console.log('[EvoriaDiscount] discountConfig:', discountConfig);

    if (!discountConfig) {
      console.log('[EvoriaDiscount] Discount', discountCode, 'not found');
      return null;
    }

    const promoHandle = discountConfig.promoHandle;
    if (!promoHandle) {
      console.log('[EvoriaDiscount] No promo handle configured for', discountCode, 'on', baseHandle);
      return null;
    }

    try {
      const response = await xhrFetch(`/products/${promoHandle}.js`);
      if (!response.ok) {
        console.log('[EvoriaDiscount] Promo product not found:', promoHandle);
        return null;
      }

      const product = await response.json();
      const variant = product.variants.find(v => v.available);

      if (!variant) {
        console.log('[EvoriaDiscount] No available variants for:', promoHandle);
        return null;
      }

      // Determine which selling plan to use for the promo variant
      let sellingPlanToUse = null;

      // Only swap if there's an exact match in the sellingPlanMap
      if (discountConfig.sellingPlanMap && originalSellingPlanId) {
        sellingPlanToUse = discountConfig.sellingPlanMap[originalSellingPlanId];
        if (sellingPlanToUse) {
          console.log('[EvoriaDiscount] Found mapped selling plan:', sellingPlanToUse, 'for original plan:', originalSellingPlanId);
        } else {
          console.log('[EvoriaDiscount] No mapping found for original plan:', originalSellingPlanId, '- will not swap');
        }
      } else {
        console.log('[EvoriaDiscount] No selling plan map or no original selling plan - will not swap one-time purchase');
      }

      // Return null if no selling plan match (don't swap one-time purchases)
      if (!sellingPlanToUse) {
        return null;
      }

      return {
        variantId: variant.id,
        handle: promoHandle,
        sellingPlanId: sellingPlanToUse
      };
    } catch (error) {
      console.error('[EvoriaDiscount] Error fetching promo variant:', error);
      return null;
    }
  }

  // ============================================================================
  // LOADING OVERLAY (full site, with polling progress)
  // ============================================================================

  let progressInterval = null;
  let currentProgress = 0;

  function showLoadingOverlay(message = 'Preparing your checkout...') {
    console.log('[EvoriaLoading] Showing loading overlay:', message);

    // Clear any existing interval
    if (progressInterval) clearInterval(progressInterval);
    currentProgress = 0;

    // Remove any existing overlay
    const existingOverlay = document.getElementById('evoria-loading-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // Create full-site overlay
    const overlay = document.createElement('div');
    overlay.id = 'evoria-loading-overlay';
    overlay.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      z-index: 999999 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: white !important;
    `;
    overlay.innerHTML = `
      <div style="display: grid; gap: 1.5rem; text-align: center;">
        <h3 style="font-weight: 600; font-size: 1.5rem; color: #000; font-family: system-ui, -apple-system, sans-serif; margin: 0;">${message}</h3>
        <p id="evoria-progress-text" style="font-size: 2rem; color: #000; margin: 0; font-weight: 600;">0%</p>
      </div>
    `;
    document.body.appendChild(overlay);

    // Start polling progress
    const text = document.getElementById('evoria-progress-text');
    if (text) {
      progressInterval = setInterval(() => {
        // Fast initially, then slow down - never decrease
        if (currentProgress < 50) {
          currentProgress += 8;
        } else if (currentProgress < 75) {
          currentProgress += 4;
        } else if (currentProgress < 90) {
          currentProgress += 2;
        } else if (currentProgress < 96) {
          // Very slow increment at the end, never going down
          currentProgress += 0.3 + Math.random() * 0.4;
        } else if (currentProgress < 99) {
          // Once at 96%, increment by 1 each interval until 99%
          currentProgress += 1;
        } else {
          // Cap at 99% (100% is set on hide)
          currentProgress = 99;
        }
        text.textContent = Math.round(currentProgress) + '%';
      }, 100);
    }
  }

  function hideLoadingOverlay() {
    console.log('[EvoriaLoading] Hiding loading overlay');

    // Stop progress polling
    if (progressInterval) {
      clearInterval(progressInterval);
      progressInterval = null;
    }

    // Finish the progress text to 100% before hiding
    const text = document.getElementById('evoria-progress-text');
    if (text) {
      text.textContent = '100%';
      // Small delay to show completion, then hide
      setTimeout(() => {
        const overlay = document.getElementById('evoria-loading-overlay');
        if (overlay) {
          overlay.style.opacity = '0';
          overlay.style.transition = 'opacity 0.15s';
          setTimeout(() => overlay.remove(), 150);
        }
      }, 150);
    } else {
      const overlay = document.getElementById('evoria-loading-overlay');
      if (overlay) overlay.remove();
    }
  }

  // Force browser to render before continuing (minimal delay)
  function yieldToRenderer() {
    return Promise.resolve();
  }

  // ============================================================================
  // CHECKOUT HANDLER - Swap to promo at checkout
  // ============================================================================

  let isProcessingCheckout = false;

  function setupCheckoutHandler() {
    // Find all checkout forms and override their submit behavior
    const checkoutForms = document.querySelectorAll('form[action*="/cart"], form[action*="/checkout"]');
    checkoutForms.forEach(form => {
      const checkoutButton = form.querySelector('[name="checkout"]');
      if (checkoutButton) {
        // Override the form's submit event entirely
        form.addEventListener('submit', async function(event) {
          // Always prevent default, we'll handle navigation ourselves
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Skip if already processing (backup handler might have started)
          if (isProcessingCheckout) {
            console.log('[EvoriaDiscount] Form submit: Already processing, skipping');
            return;
          }

          // Get current discount codes from cart
          const cart = await getCart();
          const discountCodes = (cart.discount_codes || []).map(d => d.code);

          if (discountCodes.length === 0) {
            console.log('[EvoriaDiscount] No discounts, proceeding to checkout normally');
            // Navigate directly to checkout
            window.location.href = '/checkout';
            return;
          }

          if (isProcessingCheckout) return;
          isProcessingCheckout = true;

          console.log('[EvoriaDiscount] Checkout submit with discounts:', discountCodes);

          checkoutButton.disabled = true;
          checkoutButton.classList.add('loading');

          // Show button loading state first (placebo delay before overlay)
          const randomDelay = 2000 + Math.random() * 2000; // 2-4 seconds random
          await new Promise(resolve => setTimeout(resolve, randomDelay));

          // Now show the loading overlay
          showLoadingOverlay();
          await yieldToRenderer(); // Let browser render the animation

          try {
            // First, check if there are any items that can be swapped (have matching selling plans)
            const cart = await getCart();
            const swappableItems = [];

            for (const item of cart.items) {
              console.log('[EvoriaDiscount] Checking item for swap:', item.handle);
              console.log('[EvoriaDiscount] Item has selling_plan_allocation:', !!item.selling_plan_allocation);

              // Log all keys in selling_plan_allocation to debug
              if (item.selling_plan_allocation) {
                console.log('[EvoriaDiscount] selling_plan_allocation keys:', JSON.stringify(Object.keys(item.selling_plan_allocation)));
                console.log('[EvoriaDiscount] selling_plan_allocation:', JSON.stringify(item.selling_plan_allocation));
              }

              // Try different possible property names
              const alloc = item.selling_plan_allocation;
              const originalSellingPlanId = alloc?.selling_plan_id
                || alloc?.id
                || alloc?.selling_plan?.id
                || item.selling_plan_id
                || item.properties?._selling_plan_id;

              console.log('[EvoriaDiscount] Extracted selling plan ID:', originalSellingPlanId);

              for (const discountCode of discountCodes) {
                const promo = await getPromoVariant(item.handle, discountCode, originalSellingPlanId);
                if (promo) {
                  swappableItems.push({ item, promo, discountCode });
                }
              }
            }

            // If no items can be swapped (all one-time purchases), proceed normally
            if (swappableItems.length === 0) {
              console.log('[EvoriaDiscount] No swappable items found (all one-time purchases), proceeding to checkout with discount');
              isProcessingCheckout = false;
              checkoutButton.disabled = false;
              checkoutButton.classList.remove('loading');
              // Navigate directly to checkout (keep discount in cart)
              window.location.href = '/checkout';
              return;
            }

            // We have items to swap - clear discount and proceed
            console.log('[EvoriaDiscount] Found', swappableItems.length, 'swappable item(s), clearing discount and swapping');

            // Store original cart state for restoration on return
            const originalCartState = swappableItems.map(({ item }) => ({
              handle: item.handle,
              variant_id: item.variant_id,
              quantity: item.quantity,
              selling_plan_id: item.selling_plan_allocation?.selling_plan?.id
            }));
            localStorage.setItem(ORIGINAL_CART_KEY, JSON.stringify(originalCartState));

            // Store discount code for reapply on return
            localStorage.setItem(STORAGE_KEY, discountCodes[0]);

            // Clear discount from Shopify cart
            console.log('[EvoriaDiscount] Clearing discount...');
            await clearShopifyDiscount();
            console.log('[EvoriaDiscount] Discount cleared');

            // Batch swap: Add all promos first, then remove all originals (prevents race conditions)
            console.log('[EvoriaDiscount] Batch adding promo variants...');
            await addVariantsToCart(swappableItems.map(({ item, promo }) => ({
              variantId: promo.variantId,
              quantity: item.quantity,
              sellingPlan: promo.sellingPlanId
            })));

            console.log('[EvoriaDiscount] Batch removing original items...');
            await removeItemsFromCart(swappableItems.map(({ item }) => item.key));

            // Final cleanup: Check if any swappable items remain (match by variant_id AND selling_plan_id)
            const cleanupCart = await getCart();
            const swappableSignatures = swappableItems.map(s => ({
              variantId: s.item.variant_id,
              sellingPlanId: s.item.selling_plan_allocation?.selling_plan?.id
            }));
            const remainingOriginalItems = cleanupCart.items.filter(item => {
              if (item.handle.startsWith('promotion-')) return false;
              const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
              return swappableSignatures.some(sig =>
                sig.variantId === item.variant_id &&
                sig.sellingPlanId === itemSellingPlanId
              );
            });

            if (remainingOriginalItems.length > 0) {
              console.warn('[EvoriaDiscount] Found remaining original items after batch remove, cleaning up:', remainingOriginalItems.map(i => i.key));
              await removeItemsFromCart(remainingOriginalItems.map(item => item.key));
            }

            // Brief pause for cart to settle (reduced from 1000ms to 100ms)
            await new Promise(resolve => setTimeout(resolve, 100));

            // Final verification - log cart state before navigating
            const preCheckoutCart = await getCart();
            console.log('[EvoriaDiscount] Pre-checkout cart has', preCheckoutCart.items.length, 'item(s):');
            preCheckoutCart.items.forEach((item, index) => {
              console.log(`[EvoriaDiscount]   Item ${index + 1}:`, {
                handle: item.handle,
                key: item.key,
                variant_id: item.variant_id,
                selling_plan_id: item.selling_plan_allocation?.selling_plan?.id,
                quantity: item.quantity
              });
            });

            console.log('[EvoriaDiscount] Swap complete, navigating to checkout');
            // Proceed to checkout
            window.location.href = '/checkout';
          } catch (error) {
            console.error('[EvoriaDiscount] Error during checkout swap:', error);
            isProcessingCheckout = false;
            checkoutButton.disabled = false;
            checkoutButton.classList.remove('loading');
            hideLoadingOverlay();
            // Let the form submit naturally on error
            form.submit();
          }
        }, false); // Use bubble phase to override default handlers
      }
    });

    // Add a submit event listener in capture phase to prevent form submission
    // when the backup handler is processing the swap
    document.addEventListener('submit', async function(event) {
      if (isProcessingCheckout) {
        console.log('[EvoriaDiscount] Preventing form submit, swap in progress');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    }, true); // Use capture phase

    // Also add a click handler as backup for buttons not handled by form submit
    document.addEventListener('click', async function(event) {
      const checkoutButton = event.target.closest('[name="checkout"], a[href*="/checkout"]');
      if (!checkoutButton || isProcessingCheckout) return;

      // Prevent default immediately to stop any navigation
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Get current discount codes from cart
      const cart = await getCart();
      const discountCodes = (cart.discount_codes || []).map(d => d.code);

      if (discountCodes.length === 0) {
        console.log('[EvoriaDiscount] No discounts, proceeding to checkout normally');
        // Navigate to checkout directly
        window.location.href = '/checkout';
        return;
      }

      // Already prevented default above, execute swap logic

      console.log('[EvoriaDiscount] Checkout click intercepted, executing swap logic');

      if (isProcessingCheckout) return;
      isProcessingCheckout = true;

      console.log('[EvoriaDiscount] Processing flag set, disabling button');

      // Disable button and show loading state
      checkoutButton.disabled = true;
      checkoutButton.classList.add('loading');

      // Show button loading state first (placebo delay before overlay)
      const randomDelay = 2000 + Math.random() * 2000; // 2-4 seconds random
      await new Promise(resolve => setTimeout(resolve, randomDelay));

      // Now show the loading overlay
      showLoadingOverlay();
      await yieldToRenderer(); // Let browser render the animation

      console.log('[EvoriaDiscount] Button disabled, about to get cart');

      try {
        // First, check if there are any items that can be swapped (have matching selling plans)
        const cart = await getCart();
        console.log('[EvoriaDiscount] Got cart, items:', cart.items.length);

        // Check if cart already has promo variants (from previous checkout attempt)
        const hasPromoVariants = cart.items.some(item => item.handle.startsWith('promotion-'));
        if (hasPromoVariants) {
          console.log('[EvoriaDiscount] Cart already has promo variants, clearing discount and proceeding to checkout');
          // Store discount code for reapply on return
          const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(discountCodes[0]);
          if (discountConfig) {
            localStorage.setItem(STORAGE_KEY, discountConfig.code);

            // Store current cart state for restoration on return
            // For promo variants, we need to find what ORIGINAL selling plan they correspond to
            const currentCartState = cart.items.map(item => {
              if (item.handle.startsWith('promotion-')) {
                const baseHandle = item.handle.replace(/^promotion-/, '');
                const promoSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;

                // Reverse map: find which original selling plan maps to this promo selling plan
                let originalSellingPlanId = null;
                if (discountConfig.sellingPlanMap && promoSellingPlanId) {
                  // Find the key (original plan) that has this promo plan as value
                  originalSellingPlanId = Object.keys(discountConfig.sellingPlanMap).find(
                    key => discountConfig.sellingPlanMap[key] === promoSellingPlanId
                  );
                  // Convert string key to number if needed
                  if (originalSellingPlanId) {
                    originalSellingPlanId = parseInt(originalSellingPlanId, 10);
                  }
                }

                return {
                  handle: baseHandle,
                  variant_id: null, // Will find variant when swapping back
                  quantity: item.quantity,
                  selling_plan_id: originalSellingPlanId,
                  isPromo: true
                };
              }
              return null;
            }).filter(Boolean);

            if (currentCartState.length > 0) {
              localStorage.setItem(ORIGINAL_CART_KEY, JSON.stringify(currentCartState));
              console.log('[EvoriaDiscount] Stored promo cart state for restoration:', currentCartState);
            }
          }

          // Clear discount from Shopify cart
          await clearShopifyDiscount();
          console.log('[EvoriaDiscount] Discount cleared, navigating to checkout');
          // Navigate to checkout (no swap needed, already have promo variants)
          window.location.href = '/checkout';
          return;
        }

        const swappableItems = [];

        for (const item of cart.items) {
          console.log('[EvoriaDiscount] Checking item for swap:', item.handle);
          console.log('[EvoriaDiscount] Item has selling_plan_allocation:', !!item.selling_plan_allocation);

          // Log all keys in selling_plan_allocation to debug
          if (item.selling_plan_allocation) {
            console.log('[EvoriaDiscount] selling_plan_allocation keys:', JSON.stringify(Object.keys(item.selling_plan_allocation)));
            console.log('[EvoriaDiscount] selling_plan_allocation:', JSON.stringify(item.selling_plan_allocation));
          }

          // Try different possible property names
          const alloc = item.selling_plan_allocation;
          const originalSellingPlanId = alloc?.selling_plan_id
            || alloc?.id
            || alloc?.selling_plan?.id
            || item.selling_plan_id
            || item.properties?._selling_plan_id;

          console.log('[EvoriaDiscount] Extracted selling plan ID:', originalSellingPlanId);
          console.log('[EvoriaDiscount] Discount codes to check:', discountCodes);

          for (const discountCode of discountCodes) {
            console.log('[EvoriaDiscount] Checking discount code:', discountCode);
            const promo = await getPromoVariant(item.handle, discountCode, originalSellingPlanId);
            if (promo) {
              swappableItems.push({ item, promo, discountCode });
            }
          }
        }

        // If no items can be swapped (all one-time purchases), proceed normally
        if (swappableItems.length === 0) {
          console.log('[EvoriaDiscount] No swappable items found (all one-time purchases), proceeding to checkout with discount');
          isProcessingCheckout = false;
          checkoutButton.disabled = false;

          // Navigate directly to checkout (keep discount in cart)
          window.location.href = '/checkout';
          return;
        }

        // We have items to swap - clear discount and proceed
        console.log('[EvoriaDiscount] Found', swappableItems.length, 'swappable item(s), clearing discount and swapping');

        // Store discount code for reapply on return (normalize to config format)
        const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(discountCodes[0]);
        if (discountConfig) {
          localStorage.setItem(STORAGE_KEY, discountConfig.code); // Store in config's case
        } else {
          localStorage.setItem(STORAGE_KEY, discountCodes[0]); // Fallback to what's in cart
        }

        // Store original cart state BEFORE swapping for restoration on return
        const originalCartState = swappableItems.map(({ item }) => ({
          handle: item.handle,
          variant_id: item.variant_id,
          quantity: item.quantity,
          selling_plan_id: item.selling_plan_allocation?.selling_plan?.id
        }));
        localStorage.setItem(ORIGINAL_CART_KEY, JSON.stringify(originalCartState));
        console.log('[EvoriaDiscount] Stored original cart state for restoration:', originalCartState);

        // Clear discount from Shopify cart
        console.log('[EvoriaDiscount] Clearing discount...');
        await clearShopifyDiscount();
        console.log('[EvoriaDiscount] Discount cleared');

        // Batch swap: Add all promos first, then remove all originals (prevents race conditions)
        console.log('[EvoriaDiscount] Batch adding promo variants...');
        await addVariantsToCart(swappableItems.map(({ item, promo }) => ({
          variantId: promo.variantId,
          quantity: item.quantity,
          sellingPlan: promo.sellingPlanId
        })));

        console.log('[EvoriaDiscount] Batch removing original items...');
        await removeItemsFromCart(swappableItems.map(({ item }) => item.key));

        // Final cleanup: Check if any swappable items remain (match by variant_id AND selling_plan_id)
        const cleanupCart = await getCart();
        const swappableSignatures = swappableItems.map(s => ({
          variantId: s.item.variant_id,
          sellingPlanId: s.item.selling_plan_allocation?.selling_plan?.id
        }));
        const remainingOriginalItems = cleanupCart.items.filter(item => {
          if (item.handle.startsWith('promotion-')) return false;
          const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
          return swappableSignatures.some(sig =>
            sig.variantId === item.variant_id &&
            sig.sellingPlanId === itemSellingPlanId
          );
        });

        if (remainingOriginalItems.length > 0) {
          console.warn('[EvoriaDiscount] Found remaining original items after batch remove, cleaning up:', remainingOriginalItems.map(i => i.key));
          await removeItemsFromCart(remainingOriginalItems.map(item => item.key));
        }

        // Brief pause for cart to settle (reduced from 1000ms to 100ms)
        await new Promise(resolve => setTimeout(resolve, 100));

        // Final verification - log cart state before navigating
        const preCheckoutCart = await getCart();
        console.log('[EvoriaDiscount] Pre-checkout cart has', preCheckoutCart.items.length, 'item(s):');
        preCheckoutCart.items.forEach((item, index) => {
          console.log(`[EvoriaDiscount]   Item ${index + 1}:`, {
            handle: item.handle,
            key: item.key,
            variant_id: item.variant_id,
            selling_plan_id: item.selling_plan_allocation?.selling_plan?.id,
            quantity: item.quantity
          });
        });

        // FINAL SAFETY CHECK: If cart has promo variants and discount is still applied, clear it
        const hasPromoVariantsFinal = preCheckoutCart.items.some(item => item.handle.startsWith('promotion-'));
        const hasDiscount = preCheckoutCart.discount_codes && preCheckoutCart.discount_codes.length > 0;

        if (hasPromoVariantsFinal && hasDiscount) {
          console.warn('[EvoriaDiscount] SAFETY: Cart has promo variants with discount code - clearing discount to prevent double discount');
          await clearShopifyDiscount();
          // Store discount code for reapply on return
          const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(preCheckoutCart.discount_codes[0].code);
          if (discountConfig) {
            localStorage.setItem(STORAGE_KEY, discountConfig.code);
            // Store cart state for restoration
            const cartState = preCheckoutCart.items.map(item => {
              if (item.handle.startsWith('promotion-')) {
                const baseHandle = item.handle.replace(/^promotion-/, '');
                const promoSellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
                let originalSellingPlanId = null;
                if (discountConfig.sellingPlanMap && promoSellingPlanId) {
                  originalSellingPlanId = Object.keys(discountConfig.sellingPlanMap).find(
                    key => discountConfig.sellingPlanMap[key] === promoSellingPlanId
                  );
                  if (originalSellingPlanId) {
                    originalSellingPlanId = parseInt(originalSellingPlanId, 10);
                  }
                }
                return {
                  handle: baseHandle,
                  variant_id: null,
                  quantity: item.quantity,
                  selling_plan_id: originalSellingPlanId,
                  isPromo: true
                };
              }
              return null;
            }).filter(Boolean);
            if (cartState.length > 0) {
              localStorage.setItem(ORIGINAL_CART_KEY, JSON.stringify(cartState));
            }
          }
        }

        console.log('[EvoriaDiscount] Swap complete, navigating to checkout');
        // Proceed to checkout
        window.location.href = '/checkout';
      } catch (error) {
        console.error('[EvoriaDiscount] Error during checkout swap:', error);
        isProcessingCheckout = false;
        checkoutButton.disabled = false;
        hideLoadingOverlay();
        // If it's an anchor tag, navigate naturally on error
        if (checkoutButton.tagName === 'A') {
          window.location.href = checkoutButton.href;
        } else {
          // For submit buttons, try to submit the form
          const form = checkoutButton.closest('form') || document.getElementById(checkoutButton.getAttribute('form'));
          if (form) {
            form.submit();
          }
        }
      }
    }, true);
  }

  // ============================================================================
  // RETURN FROM CHECKOUT - Swap back to regular, reapply discount
  // ============================================================================

  async function handleReturnFromCheckout() {
    // Don't run if we're currently processing checkout
    if (isProcessingCheckout) {
      console.log('[EvoriaDiscount] Checkout in progress, skipping return handler');
      return;
    }

    // Prevent multiple concurrent executions
    if (isProcessingReturn) {
      console.log('[EvoriaDiscount] Already processing return from checkout, skipping');
      return;
    }

    const storedCode = localStorage.getItem(STORAGE_KEY);
    const originalCartStateJson = localStorage.getItem(ORIGINAL_CART_KEY);
    if (!storedCode || !originalCartStateJson) return;

    console.log('[EvoriaDiscount] Returned from checkout, stored code:', storedCode);
    console.log('[EvoriaDiscount] Original cart state:', originalCartStateJson);

    // IMPORTANT: Check if cart actually has promo variants before processing
    // This prevents running multiple times across page navigations
    const cart = await getCart();
    const hasPromoVariants = cart.items.some(item => item.handle.startsWith('promotion-'));

    if (!hasPromoVariants) {
      console.log('[EvoriaDiscount] No promo variants in cart, clearing stored state and skipping');
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ORIGINAL_CART_KEY);
      return;
    }

    console.log('[EvoriaDiscount] Found promo variants in cart, proceeding with swap-back');

    // Mark as processing to prevent duplicate executions
    isProcessingReturn = true;

    // Show loading overlay during swap-back
    showLoadingOverlay('Restoring your cart...');
    await yieldToRenderer(); // Let browser render the animation

    // Validate that the stored code exists in config
    const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(storedCode);
    if (!discountConfig) {
      console.error('[EvoriaDiscount] Stored discount code not found in config:', storedCode);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ORIGINAL_CART_KEY);
      isProcessingReturn = false; // Reset flag on error
      hideLoadingOverlay();
      return;
    }
    console.log('[EvoriaDiscount] Stored discount config found:', discountConfig);

    let originalCartState;
    try {
      originalCartState = JSON.parse(originalCartStateJson);
    } catch (e) {
      console.error('[EvoriaDiscount] Error parsing original cart state:', e);
      localStorage.removeItem(ORIGINAL_CART_KEY);
      isProcessingReturn = false; // Reset flag on error
      hideLoadingOverlay();
      return;
    }

    // Group items by variant + selling_plan to handle duplicates correctly
    const itemsByVariant = new Map();
    for (const originalItem of originalCartState) {
      const key = `${originalItem.variant_id}_${originalItem.selling_plan_id || 'none'}`;
      if (!itemsByVariant.has(key)) {
        itemsByVariant.set(key, { ...originalItem, totalQuantity: 0 });
      }
      const entry = itemsByVariant.get(key);
      entry.totalQuantity += originalItem.quantity;
    }

    console.log('[EvoriaDiscount] Grouped items for swap-back:', Array.from(itemsByVariant.values()));

    // First, batch remove all promo variants
    const promoKeys = cart.items.filter(item => item.handle.startsWith('promotion-')).map(item => item.key);
    if (promoKeys.length > 0) {
      console.log('[EvoriaDiscount] Batch removing promo variants:', promoKeys.length);
      await removeItemsFromCart(promoKeys);
    }

    // Then, batch add back original items with correct quantities
    const itemsToAdd = [];
    for (const [key, item] of itemsByVariant) {
      try {
        const response = await xhrFetch(`/products/${item.handle}.js`);
        if (response.ok) {
          const product = await response.json();

          // Find the target variant
          const targetVariant = product.variants.find(v => v.id === item.variant_id);
          if (!targetVariant) {
            console.warn('[EvoriaDiscount] No suitable variant found for:', item.handle);
            continue;
          }

          console.log('[EvoriaDiscount] Swapping back to regular:', item.handle,
            'variant:', targetVariant.id,
            'selling_plan:', item.selling_plan_id || 'none',
            'quantity:', item.totalQuantity);

          itemsToAdd.push({
            variantId: targetVariant.id,
            quantity: item.totalQuantity,
            sellingPlan: item.selling_plan_id || null
          });
        }
      } catch (e) {
        console.error('[EvoriaDiscount] Error swapping back:', item.handle, e);
      }
    }

    // Batch add all original items
    if (itemsToAdd.length > 0) {
      console.log('[EvoriaDiscount] Batch adding original items:', itemsToAdd.length);
      await addVariantsToCart(itemsToAdd);
    }

    // Verify the swap was successful - get fresh cart and check
    const finalCart = await getCart();
    console.log('[EvoriaDiscount] Final cart after swap-back has', finalCart.items.length, 'items:');
    finalCart.items.forEach((item, idx) => {
      console.log(`  [${idx + 1}] ${item.handle} (var: ${item.variant_id}, plan: ${item.selling_plan_allocation?.selling_plan?.id || 'none'}, qty: ${item.quantity})`);
    });

    // Reapply discount to Shopify cart
    try {
      await applyShopifyDiscount(storedCode);
      console.log('[EvoriaDiscount] Reapplied discount:', storedCode);
    } catch (e) {
      console.error('[EvoriaDiscount] Error reapplying discount:', e);
    }

    // Clear stored state after successful reapply
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ORIGINAL_CART_KEY);

    // Reset processing flag
    isProcessingReturn = false;

    // Hide loading overlay
    hideLoadingOverlay();

    // Refresh cart UI
    refreshCartUI();
  }

  // ============================================================================
  // CART UI REFRESH
  // ============================================================================

  function refreshCartUI() {
    // Trigger cart:refresh event which the badge listens to
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // Also publish to theme's pubsub for other components
    if (window.theme && window.theme.pubsub) {
      getCart().then(cart => {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'evoria-discount',
          cart: cart
        });
      });
    }
  }

  // ============================================================================
  // CUSTOM ELEMENT: Discount Form
  // ============================================================================

  if (!customElements.get('evoria-discount-form')) {
    customElements.define(
      'evoria-discount-form',
      class EvoriaDiscountForm extends HTMLElement {
        constructor() {
          super();
          this.isProcessing = false;
        }

        connectedCallback() {
          this.form = this.querySelector('form');
          this.input = this.querySelector('[name="discount"]');
          this.button = this.querySelector('[type="submit"]');
          this.errorElement = this.querySelector('[data-error]');

          if (this.form) {
            console.log('[EvoriaDiscount] Discount form found, setting up submit handler');
            console.log('[EvoriaDiscount] Form action:', this.form.action, 'method:', this.form.method);
            // Use capture phase to intercept before any other handlers
            this.form.addEventListener('submit', this.onSubmit.bind(this), true);
          }

          this.checkExistingDiscount();
        }

        disconnectedCallback() {
          if (this.form) {
            this.form.removeEventListener('submit', this.onSubmit);
          }
        }

        async checkExistingDiscount() {
          const cart = await getCart();
          const hasDiscount = cart.discount_codes && cart.discount_codes.length > 0;
          const wrapper = this.closest('[data-discount-form-wrapper]');

          if (hasDiscount && wrapper) {
            wrapper.classList.add('hidden');
            // Also update badge to show the discount
            const badge = document.querySelector('evoria-discount-badge');
            if (badge && cart.discount_codes[0]) {
              const codeElement = badge.querySelector('[data-code]');
              if (codeElement) {
                codeElement.textContent = cart.discount_codes[0].code || cart.discount_codes[0].title;
              }
              badge.hidden = false;
            }
          } else if (wrapper) {
            wrapper.classList.remove('hidden');
          }
        }

        async onSubmit(event) {
          console.log('[EvoriaDiscount] Discount form submit triggered');
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (this.isProcessing) {
            console.log('[EvoriaDiscount] Already processing, ignoring');
            return;
          }

          const code = this.input?.value?.trim();
          console.log('[EvoriaDiscount] Submitting discount code:', code);
          if (!code) return;

          this.isProcessing = true;
          this.setError('');
          this.setLoading(true);

          try {
            const normalizedCode = code.toUpperCase().trim();

            // Validate against config
            if (!window.EvoriaDiscountConfig) {
              throw new Error('Discount configuration not loaded');
            }

            const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(normalizedCode);
            if (!discountConfig) {
              throw new Error('Invalid discount code');
            }

            // Clear existing discounts first
            const cart = await getCart();
            if (cart.discount_codes && cart.discount_codes.length > 0) {
              console.log('[EvoriaDiscount] Clearing existing discounts');
              await clearShopifyDiscount();
            }

            // Apply to Shopify cart
            await applyShopifyDiscount(normalizedCode);
            console.log('[EvoriaDiscount] Discount applied:', normalizedCode);

            // Grant access to the promotion product page for this session
            if (discountConfig.promoHandle) {
              sessionStorage.setItem('evoria_promo_access_' + discountConfig.promoHandle, 'true');
              console.log('[EvoriaDiscount] Granted promo access for:', discountConfig.promoHandle);
            }

            // Update UI - hide form, show badge
            this.input.value = '';
            const wrapper = this.closest('[data-discount-form-wrapper]');
            if (wrapper) {
              wrapper.classList.add('hidden');
            }

            // Update and show badge
            const badge = document.querySelector('evoria-discount-badge');
            if (badge) {
              const codeElement = badge.querySelector('[data-code]');
              if (codeElement) {
                codeElement.textContent = normalizedCode;
              }
              badge.hidden = false;
            }

            // Dispatch cart refresh event for other components
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          } catch (error) {
            console.error('[EvoriaDiscount] Error:', error);
            this.setError(error.message || 'Invalid discount code');
          } finally {
            this.isProcessing = false;
            this.setLoading(false);
          }
        }

        hideForm() {
          const wrapper = this.closest('[data-discount-form-wrapper]');
          if (wrapper) {
            wrapper.classList.add('hidden');
          }
        }

        setError(message) {
          if (this.errorElement) {
            this.errorElement.textContent = message;
            this.errorElement.hidden = !message;
          }
        }

        setLoading(isLoading) {
          if (this.button) {
            this.button.setAttribute('aria-busy', String(isLoading));
            this.button.disabled = isLoading;
          }
        }
      }
    );
  }

  // ============================================================================
  // CUSTOM ELEMENT: Discount Badge
  // ============================================================================

  if (!customElements.get('evoria-discount-badge')) {
    customElements.define(
      'evoria-discount-badge',
      class EvoriaDiscountBadge extends HTMLElement {
        constructor() {
          super();
          this.codeElement = null;
          this.removeButton = null;
        }

        connectedCallback() {
          this.codeElement = this.querySelector('[data-code]');
          this.removeButton = this.querySelector('[data-remove]');

          if (this.removeButton) {
            this.removeButton.addEventListener('click', this.onRemove.bind(this));
          }

          // Update on connect and when cart refreshes
          this.updateFromCart();
          document.addEventListener('cart:refresh', () => this.updateFromCart());
        }

        async updateFromCart() {
          try {
            const cart = await getCart();
            const hasDiscount = cart.discount_codes && cart.discount_codes.length > 0;

            if (hasDiscount && cart.discount_codes[0]) {
              // Use first discount code
              const code = cart.discount_codes[0].code || cart.discount_codes[0].title;
              if (this.codeElement) {
                this.codeElement.textContent = code;
              }
              this.hidden = false;
            } else {
              this.hidden = true;
            }
          } catch (e) {
            console.error('[EvoriaDiscount] Error updating badge:', e);
            this.hidden = true;
          }
        }

        async onRemove() {
          if (this.removeButton) {
            this.removeButton.setAttribute('aria-busy', 'true');
          }

          try {
            // Clear from Shopify cart
            await clearShopifyDiscount();
            localStorage.removeItem(STORAGE_KEY);

            // Update UI
            await this.updateFromCart();

            // Show form again
            const wrapper = document.querySelector('[data-discount-form-wrapper]');
            if (wrapper) {
              wrapper.classList.remove('hidden');
            }
          } catch (e) {
            console.error('[EvoriaDiscount] Error removing discount:', e);
          } finally {
            if (this.removeButton) {
              this.removeButton.setAttribute('aria-busy', 'false');
            }
          }
        }
      }
    );
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  function init() {
    console.log('[EvoriaDiscount] Initializing...');

    if (!window.EvoriaDiscountConfig) {
      console.error('[EvoriaDiscount] EvoriaDiscountConfig not loaded!');
      return;
    }

    // Setup checkout handler
    setupCheckoutHandler();

    // Setup global discount form interceptor (backup for forms inside closed details elements)
    console.log('[EvoriaDiscount] About to call setupDiscountFormInterceptor');
    setupDiscountFormInterceptor();

    // Handle return from checkout immediately on page load
    const storedCode = localStorage.getItem(STORAGE_KEY);
    const originalCartState = localStorage.getItem(ORIGINAL_CART_KEY);
    console.log('[EvoriaDiscount] Checking localStorage - storedCode:', storedCode, 'originalCartState:', originalCartState);

    if (storedCode && originalCartState && !isProcessingReturn) {
      handleReturnFromCheckout();
    }

    // Setup cart drawer open detection
    setupCartDrawerListener();

    console.log('[EvoriaDiscount] Initialized');
  }

  // Global discount form interceptor (catches all forms, even those inside closed details)
  function setupDiscountFormInterceptor() {
    console.log('[EvoriaDiscount] >>> INSIDE setupDiscountFormInterceptor <<<');
    document.addEventListener('submit', async function(event) {
    console.log('[EvoriaDiscount] >>> FORM SUBMIT <<<', event.target.tagName, event.target.className);
      const form = event.target;
      const discountInput = form.querySelector('input[name="discount"]');

      // Only intercept forms with discount input
      if (!discountInput) return;

      const discountCode = discountInput.value?.trim();
      if (!discountCode) return;

      // Check if this is our evoria-discount-form (it will handle it)
      if (form.closest('evoria-discount-form')) {
        console.log('[EvoriaDiscount] Form is inside evoria-discount-form, letting custom element handle it');
        return;
      }

      console.log('[EvoriaDiscount] Intercepting discount form (non-custom-element)');

      // Prevent default form submission
      event.preventDefault();
      event.stopPropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
      }

      try {
        const normalizedCode = discountCode.toUpperCase().trim();

        // Validate against config
        if (!window.EvoriaDiscountConfig) {
          throw new Error('Discount configuration not loaded');
        }

        const discountConfig = window.EvoriaDiscountConfig.getPromoForCode(normalizedCode);
        if (!discountConfig) {
          throw new Error('Invalid discount code');
        }

        // Apply to Shopify cart
        await applyShopifyDiscount(normalizedCode);
        console.log('[EvoriaDiscount] Discount applied to cart:', normalizedCode);

        // Grant access to the promotion product page for this session
        if (discountConfig.promoHandle) {
          sessionStorage.setItem('evoria_promo_access_' + discountConfig.promoHandle, 'true');
          console.log('[EvoriaDiscount] Granted promo access for:', discountConfig.promoHandle);
        }

        // Clear input
        discountInput.value = '';

        // Refresh cart UI
        refreshCartUI();

        // Hide form if there's a wrapper
        const wrapper = form.closest('[data-discount-form-wrapper]');
        if (wrapper) {
          wrapper.classList.add('hidden');
        }
      } catch (error) {
        console.error('[EvoriaDiscount] Error:', error);
        // Show error if there's an error element
        const errorElement = form.querySelector('[data-error]');
        if (errorElement) {
          errorElement.textContent = error.message;
          errorElement.hidden = false;
        }
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
        }
      }
    }, true); // Use capture phase to intercept before other handlers
  }

  // Setup listener for cart drawer opening
  function setupCartDrawerListener() {
    // Listen for cart drawer toggle events (common theme patterns)
    const events = ['cart:open', 'drawer:open', 'ajaxProduct:added', 'cart:refresh'];

    events.forEach(eventName => {
      document.addEventListener(eventName, function() {
        const storedCode = localStorage.getItem(STORAGE_KEY);
        const originalCartState = localStorage.getItem(ORIGINAL_CART_KEY);
        if (storedCode && originalCartState && !isProcessingReturn) {
          console.log('[EvoriaDiscount] Cart drawer opened, checking for swap-back');
          handleReturnFromCheckout();
        }
      }, false);
    });

    // Also observe DOM changes for cart drawer
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const cartDrawer = document.querySelector('.cart-drawer, [data-cart-drawer], .drawer--cart, .mini-cart');
          if (cartDrawer && cartDrawer.classList.contains('is-open') || cartDrawer.classList.contains('open') || cartDrawer.getAttribute('aria-hidden') === 'false') {
            const storedCode = localStorage.getItem(STORAGE_KEY);
            const originalCartState = localStorage.getItem(ORIGINAL_CART_KEY);
            if (storedCode && originalCartState) {
              console.log('[EvoriaDiscount] Cart drawer detected open, checking for swap-back');
              handleReturnFromCheckout();
            }
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'open']
    });
  }

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API globally
  window.EvoriaDiscount = {
    apply: async (code) => {
      await applyShopifyDiscount(code.toUpperCase());
      refreshCartUI();
    },
    remove: async () => {
      await clearShopifyDiscount();
      localStorage.removeItem(STORAGE_KEY);
      refreshCartUI();
      // Show discount form again and hide badge
      const wrapper = document.querySelector('[data-discount-form-wrapper]');
      if (wrapper) {
        wrapper.classList.remove('hidden');
      }
      const badge = document.querySelector('evoria-discount-badge');
      if (badge) {
        badge.hidden = true;
      }
    },
    getCurrentCode: () => localStorage.getItem(STORAGE_KEY)
  };

})();
